import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/auth/AuthContext';

const ExamContext = createContext(null);

// Exam configurations
export const EXAM_CONFIG = {
  UPSC: {
    id: 'UPSC',
    name: 'UPSC & State PSC',
    shortName: 'UPSC',
    description: 'Union Public Service Commission',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500',
    textColor: 'text-orange-600',
    icon: '🇮🇳',
    stages: ['Prelims', 'Mains', 'Interview'],
    examDate: '2027-05-23', // UPSC Prelims 2027 — auto-rolls forward in getDaysToExam if past
    active: true
  },
  SI: {
    id: 'SI',
    name: 'Police Sub-Inspector',
    shortName: 'SI',
    description: 'AP & TS Police Sub-Inspector',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    icon: '👮',
    stages: ['Prelims', 'Mains', 'PET/PMT'],
    examDate: '2026-12-15',
    active: true
  },
  TGPSC: {
    id: 'TGPSC',
    name: 'Telangana Group-1',
    shortName: 'TGPSC',
    description: 'Telangana Public Service Commission',
    color: 'from-rose-500 to-pink-600',
    bgColor: 'bg-rose-500',
    textColor: 'text-rose-600',
    icon: '🏛️',
    stages: ['Prelims', 'Mains', 'Interview'],
    examDate: '2026-07-15',
    active: false
  },
  APPSC: {
    id: 'APPSC',
    name: 'Andhra Pradesh Group-1',
    shortName: 'APPSC',
    description: 'Andhra Pradesh Public Service Commission',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-600',
    icon: '⚓',
    stages: ['Screening', 'Mains', 'Interview'],
    examDate: '2026-08-20',
    active: false
  },
  BANKING: {
    id: 'BANKING',
    name: 'Banking Exams',
    shortName: 'Banking',
    description: 'IBPS PO, SBI PO, RBI Grade B & Clerk',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-600',
    icon: '🏦',
    stages: ['Prelims', 'Mains'],
    examDate: '2026-06-15',
    active: false
  }
};

// Banking Exams Syllabus Structure
export const BANKING_SYLLABUS = {
  prelims: {
    name: 'Preliminary Examination',
    papers: [
      {
        id: 'quant',
        name: 'Quantitative Aptitude',
        marks: 35,
        duration: '20 mins',
        type: 'Objective',
        questions: 35,
        sections: [
          'Number Series',
          'Simplification & Approximation',
          'Data Interpretation',
          'Quadratic Equations',
          'Arithmetic (Percentage, Profit/Loss, SI/CI, Time & Work, Speed & Distance)',
          'Mensuration & Geometry',
          'Probability & Permutation-Combination'
        ]
      },
      {
        id: 'reasoning',
        name: 'Reasoning Ability',
        marks: 35,
        duration: '20 mins',
        type: 'Objective',
        questions: 35,
        sections: [
          'Seating Arrangement (Linear & Circular)',
          'Puzzles (Floor, Box, Scheduling)',
          'Syllogism',
          'Blood Relations',
          'Direction Sense',
          'Coding-Decoding',
          'Inequality',
          'Input-Output',
          'Data Sufficiency'
        ]
      },
      {
        id: 'english',
        name: 'English Language',
        marks: 30,
        duration: '20 mins',
        type: 'Objective',
        questions: 30,
        sections: [
          'Reading Comprehension',
          'Cloze Test',
          'Error Spotting',
          'Sentence Improvement',
          'Para Jumbles',
          'Fill in the Blanks',
          'Vocabulary'
        ]
      }
    ]
  },
  mains: {
    name: 'Main Examination',
    totalMarks: 200,
    papers: [
      {
        id: 'reasoning-main',
        name: 'Reasoning & Computer Aptitude',
        marks: 60,
        duration: '45 mins',
        type: 'Objective',
        sections: [
          'Advanced Puzzles',
          'Machine Input-Output',
          'Logical Reasoning',
          'Critical Reasoning',
          'Computer Fundamentals',
          'MS Office & Internet',
          'Networking Basics'
        ]
      },
      {
        id: 'english-main',
        name: 'English Language',
        marks: 40,
        duration: '35 mins',
        type: 'Objective',
        sections: [
          'Reading Comprehension (3-4 passages)',
          'Vocabulary Based Questions',
          'Grammar & Sentence Structure',
          'Paragraph Completion',
          'Letter/Essay Writing (Descriptive)'
        ]
      },
      {
        id: 'quant-main',
        name: 'Data Analysis & Interpretation',
        marks: 60,
        duration: '45 mins',
        type: 'Objective',
        sections: [
          'Data Interpretation (Tabular, Bar, Line, Pie, Radar)',
          'Data Sufficiency',
          'Missing DI',
          'Caselet DI',
          'Advanced Arithmetic'
        ]
      },
      {
        id: 'ga',
        name: 'General/Economy/Banking Awareness',
        marks: 40,
        duration: '35 mins',
        type: 'Objective',
        sections: [
          'Banking Awareness (RBI, SEBI, NABARD)',
          'Financial Awareness (Budget, Schemes)',
          'Current Affairs (6 months)',
          'Static GK (Headquarters, Currencies, Appointments)',
          'Economy & Market News'
        ]
      }
    ]
  },
  // Banking Awareness Capsule Topics (CA Sprint Engine)
  bankingAwareness: {
    name: 'Banking Awareness Capsules',
    topics: [
      { id: 'rbi', name: 'RBI & Monetary Policy', subtopics: ['Repo Rate', 'CRR/SLR', 'LAF', 'MSF', 'Monetary Policy Committee'] },
      { id: 'banking-acts', name: 'Banking Acts & Regulations', subtopics: ['Banking Regulation Act', 'RBI Act', 'NI Act', 'SARFAESI'] },
      { id: 'products', name: 'Banking Products', subtopics: ['Savings/Current Account', 'FD/RD', 'Loans', 'Insurance', 'Mutual Funds'] },
      { id: 'digital', name: 'Digital Banking', subtopics: ['UPI', 'NEFT/RTGS/IMPS', 'BBPS', 'E-wallet', 'CBDC'] },
      { id: 'schemes', name: 'Government Schemes', subtopics: ['PMJDY', 'Mudra', 'Stand Up India', 'Atal Pension', 'PM Vishwakarma'] },
      { id: 'institutions', name: 'Financial Institutions', subtopics: ['NABARD', 'SIDBI', 'EXIM', 'NHB', 'DICGC'] },
      { id: 'markets', name: 'Financial Markets', subtopics: ['Money Market', 'Capital Market', 'Forex Market', 'Derivatives'] },
      { id: 'current', name: 'Current Banking Affairs', subtopics: ['Mergers', 'New Policies', 'Appointments', 'Awards'] }
    ]
  }
};

// TGPSC Syllabus Structure
export const TGPSC_SYLLABUS = {
  prelims: {
    name: 'Preliminary Examination',
    papers: [
      {
        id: 'gs-mental',
        name: 'General Studies & Mental Ability',
        marks: 150,
        duration: '2.5 hours',
        type: 'Objective',
        questions: 150,
        sections: [
          'Current Affairs - Regional, National & International',
          'International Relations and Events',
          'General Science & India\'s Achievements in S&T',
          'Environmental Issues & Disaster Management',
          'Economic and Social Development of India',
          'World Geography, Indian Geography & Telangana Geography',
          'History and Cultural Heritage of India',
          'Indian Constitution and Polity',
          'Governance and Public Policy',
          'Policies of Telangana State',
          'Society, Culture, Heritage, Arts & Literature of Telangana',
          'Social Exclusion & Rights Issues',
          'Logical Reasoning, Analytical Ability & Data Interpretation'
        ]
      }
    ]
  },
  mains: {
    name: 'Main Examination',
    totalMarks: 900,
    papers: [
      {
        id: 'english',
        name: 'General English',
        marks: null,
        duration: '3 hours',
        type: 'Qualifying',
        isQualifying: true,
        sections: [
          'Spotting Errors – Spelling, Punctuation',
          'Fill in the blanks - Prepositions, Conjunctions, Verb tenses',
          'Re-writing sentences – Active/Passive, Direct/Reported Speech',
          'Jumbled sentences',
          'Comprehension',
          'Précis Writing',
          'Expansion',
          'Letter Writing'
        ]
      },
      {
        id: 'paper1',
        name: 'Paper I - General Essay',
        marks: 150,
        duration: '3 hours',
        type: 'Descriptive',
        sections: [
          {
            name: 'Section I',
            topics: ['Contemporary Social Issues and Problems', 'Issues of Economic Growth and Justice']
          },
          {
            name: 'Section II', 
            topics: ['Dynamics of Indian Politics', 'Historical and Cultural Heritage of India']
          },
          {
            name: 'Section III',
            topics: ['Developments in Science and Technology', 'Education and Human Resource Development']
          }
        ],
        note: 'Write 3 essays, one from each section (50 marks each)'
      },
      {
        id: 'paper2',
        name: 'Paper II - History, Culture & Geography',
        marks: 150,
        duration: '3 hours',
        type: 'Descriptive',
        sections: [
          'History and Culture of India (1757-1947)',
          'History and Cultural Heritage of Telangana',
          'Geography of India and Telangana'
        ]
      },
      {
        id: 'paper3',
        name: 'Paper III - Indian Society, Constitution & Governance',
        marks: 150,
        duration: '3 hours',
        type: 'Descriptive',
        sections: [
          'Indian Society - Structure, Issues & Social Movements',
          'Constitution of India',
          'Governance'
        ]
      },
      {
        id: 'paper4',
        name: 'Paper IV - Economy & Development',
        marks: 150,
        duration: '3 hours',
        type: 'Descriptive',
        sections: [
          'Indian Economy and Development',
          'Telangana Economy',
          'Development and Environmental Problems'
        ]
      },
      {
        id: 'paper5',
        name: 'Paper V - Science & Technology + Data Interpretation',
        marks: 150,
        duration: '3 hours',
        type: 'Descriptive',
        sections: [
          'Role and Impact of Science & Technology',
          'Modern Trends in Science Application',
          'Data Interpretation and Problem Solving'
        ]
      },
      {
        id: 'paper6',
        name: 'Paper VI - Telangana Movement & State Formation',
        marks: 150,
        duration: '3 hours',
        type: 'Descriptive',
        sections: [
          'The Idea of Telangana (1948-1970)',
          'Mobilisational Phase (1971-1990)',
          'Towards Formation of Telangana State (1991-2014)'
        ]
      }
    ]
  }
};

export const ExamProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedExam, setSelectedExam] = useState(null);
  const [showExamSelector, setShowExamSelector] = useState(false);

  // Load selected exam from localStorage or user preference
  useEffect(() => {
    if (user) {
      const savedExam = localStorage.getItem(`clarias_exam_${user.id}`);
      if (savedExam && EXAM_CONFIG[savedExam]) {
        setSelectedExam(savedExam);
      } else {
        // Show exam selector for new users or if no exam selected
        setShowExamSelector(true);
      }
    }
  }, [user]);

  const selectExam = (examId) => {
    if (EXAM_CONFIG[examId]) {
      setSelectedExam(examId);
      if (user) {
        localStorage.setItem(`clarias_exam_${user.id}`, examId);
      }
      setShowExamSelector(false);
    }
  };

  const switchExam = () => {
    setShowExamSelector(true);
  };

  const getExamConfig = () => {
    return selectedExam ? EXAM_CONFIG[selectedExam] : null;
  };

  const getSyllabus = () => {
    if (selectedExam === 'TGPSC' || selectedExam === 'APPSC') {
      return TGPSC_SYLLABUS;
    }
    if (selectedExam === 'BANKING') {
      return BANKING_SYLLABUS;
    }
    return null;
  };

  const getDaysToExam = () => {
    const config = getExamConfig();
    if (!config?.examDate) return null;
    const examDate = new Date(config.examDate);
    examDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Auto-rollover: if the configured exam date is in the past, advance year-by-year
    // so the countdown stays meaningful even if we forget to update the constant.
    while (examDate < today) {
      examDate.setFullYear(examDate.getFullYear() + 1);
    }
    const diffDays = Math.ceil((examDate - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Exam-aware PRO entitlement. Mirrors backend `user_has_exam_pro`:
  //  - Admins → PRO everywhere.
  //  - SI context → requires "SI" in pro_exams (SI is isolated; global UPSC PRO
  //    does NOT unlock SI, and SI does NOT unlock UPSC).
  //  - UPSC/other → legacy global PRO OR an explicit exam entitlement.
  const _proExams = user?.pro_exams || [];
  const _isAdmin = user?.role === 'ADMIN' || user?.is_admin === true;
  const examIsPro = _isAdmin || (
    selectedExam === 'SI'
      ? _proExams.includes('SI')
      : (user?.role === 'PRO' || user?.subscription_status === 'PRO' || _proExams.includes(selectedExam))
  );

  return (
    <ExamContext.Provider value={{
      selectedExam,
      selectExam,
      switchExam,
      showExamSelector,
      setShowExamSelector,
      getExamConfig,
      getSyllabus,
      getDaysToExam,
      examConfig: EXAM_CONFIG,
      isPro: examIsPro,
      isUPSC: selectedExam === 'UPSC',
      isSI: selectedExam === 'SI',
      isTGPSC: selectedExam === 'TGPSC',
      isAPPSC: selectedExam === 'APPSC',
      isBanking: selectedExam === 'BANKING'
    }}>
      {children}
    </ExamContext.Provider>
  );
};

export const useExam = () => {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error('useExam must be used within an ExamProvider');
  }
  return context;
};

export default ExamContext;
